'use client';
import { useState, useCallback, useRef } from 'react';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_IMAGES_ONLY,
  MAX_FILE_SIZE,
  MAX_VIDEO_SIZE,
  ALLOWED_VIDEO_TYPES,
} from './constants';
import { IFile, IFileMeta, T_FILE_MIME_ENUM_TYPE } from './types';
import { generateUniqueId } from '@ui/constants';
import { useToast } from '@ui/components';
import { sanitizeFilename } from '@ui/lib/utils';

interface Props {
  maxFiles?: number;
  allowVideos?: boolean; // Controls whether videos are allowed in this context
}

export function useFiles({ maxFiles = 4, allowVideos = false }: Props = {}) {
  const toast = useToast();
  const [files, setFiles] = useState<IFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const createdObjectUrlsRef = useRef<Set<string>>(new Set());

  const isBlobUrl = (url: string | undefined) => !!url && url.startsWith('blob:');

  const readVideoDuration = (url: string) =>
    new Promise<number>((resolve, reject) => {
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.src = url;
      const onLoaded = () => {
        resolve(videoEl.duration || 0);
        cleanup();
      };
      const onError = () => {
        reject(new Error('Failed to read video metadata'));
        cleanup();
      };
      const cleanup = () => {
        videoEl.removeEventListener('loadedmetadata', onLoaded);
        videoEl.removeEventListener('error', onError);
      };
      videoEl.addEventListener('loadedmetadata', onLoaded);
      videoEl.addEventListener('error', onError);
    });

  const processFileMeta = useCallback(
    (fileMeta: IFileMeta): IFile | null => {
      const allowedTypes = allowVideos ? ALLOWED_FILE_TYPES : ALLOWED_FILE_TYPES_IMAGES_ONLY;
      const acceptedFormats = allowVideos
        ? 'PNG, JPG, JPEG, GIF, MP4, MOV, WebM'
        : 'PNG, JPG, JPEG, GIF';

      if (!allowedTypes.includes(fileMeta.mime_type as any)) {
        setError(`Unsupported file type. Acceptable formats: ${acceptedFormats}.`);
        return null;
      }

      const isVideo = ALLOWED_VIDEO_TYPES.includes(fileMeta.mime_type as any);

      // If video is not allowed but user tried to upload one
      if (isVideo && !allowVideos) {
        setError(`Videos are not allowed in this context. Acceptable formats: ${acceptedFormats}.`);
        return null;
      }

      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
      const sizeLabel = isVideo ? '100MB' : '10MB';

      if (fileMeta.size > maxSize) {
        setError(`File size exceeds ${sizeLabel}. Please upload a smaller file.`);
        return null;
      }

      // Front-end hint: videos must be 2 minutes or less
      if (isVideo) {
        // This displays a helpful message early; real enforcement happens in onSelectFiles
        // after we measure duration via metadata.
        setError(null);
      }

      return {
        src: fileMeta.source,
        file: null,
        id: generateUniqueId(),
        meta: fileMeta,
      };
    },
    [allowVideos],
  );

  const onSelectFiles = useCallback(
    async (fileList: FileList) => {
      const newFiles: IFile[] = [];
      const totalFiles = files.length + fileList.length;

      for (const file of Array.from(fileList)) {
        const sanitizedFilename = sanitizeFilename(file.name);

        const sanitizedFile = new File([file], sanitizedFilename, {
          type: file.type,
          lastModified: file.lastModified,
        });

        const meta: IFileMeta = {
          mime_type: sanitizedFile.type as T_FILE_MIME_ENUM_TYPE,
          name: sanitizedFile.name,
          size: sanitizedFile.size,
          source: URL.createObjectURL(sanitizedFile),
        };
        createdObjectUrlsRef.current.add(meta.source);

        // If video, validate duration via metadata (<= 120s)
        const isVideo = ALLOWED_VIDEO_TYPES.includes(meta.mime_type as any);
        if (isVideo) {
          try {
            const duration = await readVideoDuration(meta.source);

            if (duration > 90) {
              setError('Video must be 1 minute 30 seconds or less (90s).');
              continue; // skip this file
            }
          } catch {
            setError('Unable to verify video duration. Please try another file.');
            continue;
          }
        }

        const processedFile = processFileMeta(meta);
        if (processedFile) {
          newFiles.push({ ...processedFile, file: sanitizedFile });
        }

        if (files.length + newFiles.length >= maxFiles) {
          break;
        }
      }

      if (totalFiles > maxFiles) {
        toast(
          { title: `You can't upload more than ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'}` },
          { type: 'warning' },
        );
      }

      if (newFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        setError(null);
      }
    },
    [files, processFileMeta, toast, maxFiles],
  );

  const addInitialFiles = useCallback(
    (initialFiles: IFileMeta[]) => {
      const newFiles: IFile[] = [];

      for (const fileMeta of initialFiles) {
        const processedFile = processFileMeta(fileMeta);
        if (processedFile) {
          newFiles.push({ ...processedFile, preventUpload: true });
        }

        if (files.length + newFiles.length >= maxFiles) {
          break;
        }
      }

      if (newFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        setError(null);
      }
    },
    [files, processFileMeta, maxFiles],
  );

  const deleteFile = useCallback((id: string) => {
    setFiles((prevFiles) => {
      const target = prevFiles.find((f) => f.id === id);
      if (target && isBlobUrl(target.src) && createdObjectUrlsRef.current.has(target.src)) {
        URL.revokeObjectURL(target.src);
        createdObjectUrlsRef.current.delete(target.src);
      }
      return prevFiles.filter((file) => file.id !== id);
    });
    setError(null);
  }, []);

  const onDiscardFiles = useCallback(() => {
    setFiles((prevFiles) => {
      prevFiles.forEach((f) => {
        if (isBlobUrl(f.src) && createdObjectUrlsRef.current.has(f.src)) {
          URL.revokeObjectURL(f.src);
          createdObjectUrlsRef.current.delete(f.src);
        }
      });
      return [];
    });
    setError(null);
  }, []);

  return {
    files,
    onSelectFiles,
    addInitialFiles,
    deleteFile,
    error,
    onDiscardFiles,
  };
}
